import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { Skeleton } from 'antd'
import { UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import {
  parseAndDownloadCSV,
  GrPrice,
  orderedExportData,
  replaceArrayValue,
  bulkRenameObjectProperty,
} from '../../../../../utilities/helpers'
import { ReportFilter } from '../ReportFilter'
import { ReportFilterStore } from '../ReportFilterStore'
import Helmet from 'react-helmet'
import {
  ReportDirection,
  ReportFields,
  IReportV2Query,
  IBaseReportData,
} from '@getgreenline/homi-shared/dist/models/Reports/ReportsV2'
import { API } from '@getgreenline/homi-shared'
import { Track } from '../../../../../Track'
import moment from 'moment'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'

interface Props {
  companyId: number
  reportFilterStore?: ReportFilterStore
  currentCompanyStore?: CurrentCompanyStore
}

interface IOrder {
  field: ReportFields
  direction: ReportDirection
}

interface State {
  locationGroupBy: 'all' | 'per location'
  productFormat: 'nested' | 'parent'
  order: IOrder
  results?: IBaseReportData[]
  isLoading: boolean
}

@inject('reportFilterStore', 'currentCompanyStore')
@observer
export class ProductSalesReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      results: undefined,
      locationGroupBy: 'all',
      productFormat: 'nested',
      order: {
        field: 'Net sales',
        direction: 'desc',
      },
      isLoading: false,
    }
  }

  componentDidMount = async () => {
    await this.getResults()
  }

  get fields() {
    const product = this.state.productFormat === 'nested' ? 'Product' : 'Parent product'
    const fields: ReportFields[] = [
      'Location',
      'SKU',
      product,
      'Category',
      'Brand',
      'Quantity',
      'Gross sales',
      'Refunds',
      'Discounts',
      'Net sales',
      'Cost',
      'Profit',
      'Margin',
    ]

    return fields
  }

  get query() {
    const { selectedLocationIds, startDateString, endDateString } = this.props.reportFilterStore!
    const { locationGroupBy, productFormat, order } = this.state
    const groupByDimension = productFormat === 'nested' ? 'Product' : 'Parent product'

    const orderByField =
      order.field === 'Product' && productFormat === 'parent' ? 'Parent product' : order.field

    const query: IReportV2Query = {
      locationIds: selectedLocationIds,
      startDate: startDateString,
      endDate: endDateString,
      groupBy: {
        time: null,
        dimensions: locationGroupBy === 'all' ? [groupByDimension] : ['Location', groupByDimension],
      },
      orderBy: {
        ...this.state.order,
        field: orderByField,
      },
      fields: replaceArrayValue(this.fields, 'Brand', 'Vendor'),
    }

    return query
  }

  getResults = async () => {
    const { companyId } = this.props
    const { startDate, endDate } = this.props.reportFilterStore!
    await this.setState({ isLoading: true, results: [] })

    try {
      Track.startTimer('dashboard:reports_productsOverview')
      const apiResults = await API.getSales(companyId, this.query)
      const results = bulkRenameObjectProperty(apiResults, 'Vendor', 'Brand')
      Track.track('dashboard:reports_productsOverview', {
        numberOfDays: moment(endDate).diff(startDate, 'days'),
      })
      this.setState({ results, isLoading: false })
    } catch (error) {
      this.setState({ results: undefined, isLoading: false })
    }
  }

  toggleOrder = async (fieldName: ReportFields) => {
    if (this.state.order.field === fieldName) {
      const currentDirection = this.state.order.direction
      this.setState({
        order: {
          field: fieldName,
          direction: currentDirection === 'asc' ? 'desc' : 'asc',
        },
      })
    } else {
      this.setState({
        order: {
          field: fieldName,
          direction: 'desc',
        },
      })
    }
    await this.getResults()
  }

  tableRenderer = (rawData?: IBaseReportData[]) => {
    if (!rawData) {
      return <div>Error</div>
    }
    if (rawData.length > 0) {
      return (
        <table className='table table-sm'>
          <thead>
            <tr>
              {this.state.locationGroupBy !== 'all' && (
                <th>
                  <a onClick={() => this.toggleOrder('Location')}>Location</a>
                </th>
              )}
              <th>
                <a onClick={() => this.toggleOrder('SKU')}>SKU</a>
              </th>
              <th>
                <a onClick={() => this.toggleOrder('Product')}>Product</a>
              </th>
              <th>
                <a onClick={() => this.toggleOrder('Category')}>Category</a>
              </th>
              <th>
                <a onClick={() => this.toggleOrder('Vendor')}>Brand</a>
              </th>
              <th>
                <a onClick={() => this.toggleOrder('Quantity')}>Qty</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('Gross sales')}>Gross</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('Refunds')}>Refunds</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('Discounts')}>Discounts</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('Net sales')}>Net</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('Cost')}>Cost</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('Profit')}>Profit</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('Margin')}>Margin</a>
              </th>
            </tr>
          </thead>
          <tbody>
            {rawData.map((data: IBaseReportData, index: number) => {
              return (
                <tr key={index}>
                  {this.state.locationGroupBy !== 'all' && <td>{data['Location']}</td>}
                  <td>{data['SKU'] || '-'}</td>
                  <td>{data['Product'] || data['Parent product']}</td>
                  <td>{data['Category']}</td>
                  <td>{data['Brand']}</td>
                  <td className='text-right'>{data['Quantity']}</td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['Gross sales'])}
                  </td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Refunds'])}</td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Discounts'])}</td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Net sales'])}</td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Cost'])}</td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Profit'])}</td>
                  <td className='text-right'>{data['Margin']}%</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )
    } else {
      return <div>No data</div>
    }
  }

  exportCSV() {
    const { startDateString, endDateString } = this.props.reportFilterStore!
    const fields = this.fields
    const results = this.state.results
    const data = orderedExportData(fields, results, undefined)
    if (data) {
      parseAndDownloadCSV(`product-sales-${startDateString}-${endDateString}`, data)
    }
  }

  render() {
    return (
      <>
        <Helmet>
          <title>Reports | Product Sales</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ReportFilter maxRangeMonths={12} />
        <div className='rounded background-white'>
          <div className='p-3'>
            <div>
              <b>Product sales</b>
            </div>
            <div className='text-muted'>Overview</div>

            <div className='mt-2 mb-2'>
              <UncontrolledButtonDropdown className='mr-2'>
                <DropdownToggle caret size='sm' color='light' style={{ textAlign: 'left' }}>
                  {this.state.locationGroupBy === 'all' ? 'Grouped locations' : 'Per location'}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem
                    onClick={async () =>
                      this.setState({ locationGroupBy: 'all' }, await this.getResults)
                    }
                  >
                    Grouped locations
                  </DropdownItem>
                  <DropdownItem
                    onClick={async () =>
                      this.setState({ locationGroupBy: 'per location' }, await this.getResults)
                    }
                  >
                    Per location
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
              <UncontrolledButtonDropdown className='mr-2'>
                <DropdownToggle caret size='sm' color='light' style={{ textAlign: 'left' }}>
                  {this.state.productFormat === 'nested' ? 'Nested' : 'Parent'}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem
                    onClick={async () =>
                      this.setState({ productFormat: 'nested' }, await this.getResults)
                    }
                  >
                    Nested
                  </DropdownItem>
                  <DropdownItem
                    onClick={async () =>
                      this.setState({ productFormat: 'parent' }, await this.getResults)
                    }
                  >
                    Parent only
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
              <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
                Export as csv
              </button>
            </div>

            <div className='table-responsive'>
              {this.state.isLoading ? <Skeleton active /> : this.tableRenderer(this.state.results)}
            </div>
          </div>
        </div>
      </>
    )
  }
}
